import type { MetricsBlock, UnattributedBooks } from '../../api/types.js';
import { accentFor, color, font, fontSize, radius, seriesColor } from '../../theme/tokens.js';
import { DataQualityBadge } from '../DataQualityBadge/DataQualityBadge.js';
import { Disclosure } from '../Disclosure/Disclosure.js';
import { ProvenanceFootnote } from '../Breakdown/Breakdown.js';
import { provenanceFor } from '../Breakdown/provenance.js';
import { formatValue } from '../MetricCell/MetricCell.js';

/**
 * The Region Detail summary bar (FR-036, FR-062, PRD §6.3, Principle I).
 *
 * Region code badge, name, leaders, and **six** summary cells, laid out as a one-pixel-gap grid
 * so the hairline background shows through as dividers.
 *
 * Two of the six are **paired** figures — `Nursing / Allied` and `Pending / Prospect` — which
 * is why the cells render their own value rather than delegating to `<MetricCell>`: that
 * component renders one number and one trend arrow, and the blueprint's cells show a pair and no
 * arrow. The tooltip still goes through the shared `<Disclosure>`, so hover and keyboard focus
 * behave exactly as they do everywhere else (FR-062).
 */

export interface RegionSummaryProps {
  regionCode: string;
  regionName: string;
  isSpecial: boolean;
  leaders: string[];
  states: string[];
  metrics: MetricsBlock;
  unattributedBooks: UnattributedBooks | null | undefined;
}

interface Stat {
  label: string;
  /** The accessible name and tooltip — the cell shows a short code, this says what it means. */
  full: string;
  /**
   * The served metric this cell reports, for the tooltip's lineage footer (2026-09-10).
   *
   * ⚠ The two cells that print a **pair** — `Nursing / Allied` and `Pending / Prospect` — key on
   * their own entries rather than on the left-hand figure's, so the sentence describes both
   * halves of what is on screen rather than half of it.
   */
  metricKey: string;
  value: (m: MetricsBlock) => string;
  sub: (m: MetricsBlock) => string;
  color?: string;
  dataQuality?: boolean;
}

const n = (m: MetricsBlock, key: string) => formatValue(m[key]?.value);

/** `a / b`, the shape the blueprint uses for the two paired cells. */
const pair = (m: MetricsBlock, a: string, b: string) => `${n(m, a)} / ${n(m, b)}`;

/** `+12` / `−4` for a 30-day change, or nothing at all where there is no comparable window. */
function delta30(m: MetricsBlock, key: string): string {
  const d = m[key]?.d30;
  if (d === null || d === undefined || Number.isNaN(d)) return 'no 30d comparison';
  return `${d > 0 ? '+' : d < 0 ? '−' : ''}${Math.abs(d)}% 30d`;
}

/**
 * The blueprint's cells, in its order — its last two removed.
 *
 * ⚠ **`Velocity 30d` and `Avg Assign / Ext` are gone** (owner request 2026-08-31), a deliberate
 * Principle I deviation. The region document carries no velocity rate and no assignment-length or
 * extension figures, so they rendered a permanent `—` under a "no source" subtext: two of eight
 * cells spending their width saying nothing, on a bar where every other cell answers. Restoring
 * them means adding the metrics first, not the cells. The grid tracks off `STATS.length`, so the
 * remaining six widen to fill the bar with no layout change here.
 */
const STATS: Stat[] = [
  {
    label: 'Recruiters',
    full: 'Recruiters in this region',
    metricKey: 'recruiters',
    value: (m) => n(m, 'recruiters'),
    sub: (m) => `${n(m, 'managerCount')} mgrs`,
  },
  {
    label: 'Nursing / Allied',
    full: 'Recruiters by offering — Nursing / Allied',
    metricKey: 'recruitersByOffering',
    value: (m) => pair(m, 'recruitersNursing', 'recruitersAllied'),
    sub: () => 'by type',
  },
  {
    label: 'Target Accts',
    full: 'Target Accounts (Primary + Secondary)',
    metricKey: 'targetAccounts',
    value: (m) => n(m, 'targetAccounts'),
    sub: () => 'primary + secondary',
  },
  {
    label: 'Book of Business',
    full: 'Total Candidates in BoB',
    metricKey: 'bob',
    value: (m) => n(m, 'bob'),
    sub: (m) => delta30(m, 'bob'),
    dataQuality: true,
  },
  {
    label: 'Active TOA',
    full: 'Active TOA — Travelers on Assignment, as-of',
    metricKey: 'activeToa',
    value: (m) => n(m, 'activeToa'),
    /*
     * Since 2026-09-03 this pairs two figures off the **same** table: TOA is placements that
     * have started, ONB is placements booked and not yet started (PRD §9.8.3a) — the pipeline
     * feeding the headline. It previously paired a placement stock with a candidate-status
     * count, which is why the subtext could not be read as "and this many more are coming".
     */
    sub: (m) => `${n(m, 'onboarding')} onboarding`,
    color: seriesColor.toa,
  },
  {
    label: 'Pending / Prospect',
    full: 'Candidates Pending / Prospect',
    metricKey: 'pendingProspect',
    value: (m) => pair(m, 'pending', 'prospect'),
    sub: () => 'in pipeline',
    color: seriesColor.jobs,
    dataQuality: true,
  },
];

export function RegionSummary({
  regionCode,
  regionName,
  isSpecial,
  leaders,
  states,
  metrics,
  unattributedBooks,
}: RegionSummaryProps) {
  const accent = accentFor(regionCode);

  return (
    <section
      aria-label={`${regionName} summary`}
      style={{
        background: color.surface,
        border: `1px solid ${color.border}`,
        borderLeft: `4px solid ${accent}`,
        borderRadius: radius['6xl'],
        padding: '20px 24px',
        marginBottom: 18,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span
          className="mono"
          style={{
            fontFamily: font.mono,
            fontSize: fontSize.control,
            fontWeight: 600,
            color: color.onAccent,
            background: accent,
            padding: '3px 10px',
            borderRadius: radius.lg,
          }}
        >
          {regionCode}
        </span>
        <h2
          style={{
            margin: 0,
            fontSize: fontSize.detailTitle,
            fontWeight: 700,
            letterSpacing: '-.4px',
          }}
        >
          {regionName}
        </h2>
        {isSpecial ? (
          <span
            style={{
              fontSize: '10px',
              fontWeight: 600,
              letterSpacing: '.4px',
              color: color.warning,
              background: color.noticeBg,
              border: '1px solid #f6dcae',
              padding: '2px 7px',
              borderRadius: radius.sm,
              textTransform: 'uppercase',
            }}
          >
            Special Project
          </span>
        ) : null}
        <span style={{ fontSize: fontSize.bodyStrong, color: color.inkSubtle }}>
          Leader · {leaders.length > 0 ? leaders.join(', ') : '—'}
        </span>
        {states.length > 0 ? (
          <span
            className="mono"
            style={{ fontFamily: font.mono, fontSize: fontSize.caption, color: color.inkFaint }}
          >
            {states.join(' · ')}
          </span>
        ) : null}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${STATS.length}, minmax(90px, 1fr))`,
          gap: 1,
          background: color.borderSubtle,
          border: `1px solid ${color.borderSubtle}`,
          borderRadius: radius['3xl'],
          overflow: 'hidden',
          marginTop: 16,
        }}
      >
        {STATS.map((stat) => (
          <div key={stat.label} style={{ background: color.surface, padding: '12px 13px' }}>
            <div
              style={{
                fontSize: '10px',
                fontWeight: 600,
                letterSpacing: '.3px',
                color: color.inkFaint,
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              {stat.label}
              {stat.dataQuality ? (
                <DataQualityBadge
                  unattributedBooks={unattributedBooks}
                  figureLabel={stat.full}
                />
              ) : null}
            </div>
            <Disclosure
              accessibleName={`${stat.full}: ${stat.value(metrics)}`}
              panel={
                <span style={{ display: 'block' }}>
                  <strong>{stat.full}</strong>
                  <span style={{ display: 'block' }}>{stat.value(metrics)}</span>
                  <ProvenanceFootnote provenance={provenanceFor(stat.metricKey)} />
                </span>
              }
            >
              <span
                className="mono"
                style={{
                  fontFamily: font.mono,
                  fontSize: fontSize.statValue,
                  fontWeight: 600,
                  color: stat.color ?? color.ink,
                }}
              >
                {stat.value(metrics)}
              </span>
            </Disclosure>
            <div style={{ fontSize: '10.5px', color: color.inkHint, marginTop: 1 }}>
              {stat.sub(metrics)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
